import styled from '@emotion/styled/macro';
import { Variables } from '../base/variables';

type Tone = 'electronic' | 'paper' | 'blocked';

/**
 * Foreground and background come from opposite ends of a ramp.
 *
 * `Warning*` is the RED family here and `Attention*` is ORANGE — the names do not say so, and
 * the design's "Not routed" and HALTED are red.
 *
 * `*Mild*` and `*Medium*` are BOTH surface ramps: `AttentionMedium1` #f8b283 on
 * `AttentionMild1` #fad6b8 is about 1.2:1, which is how these chips were briefly unreadable.
 * Accent text lives at `*Inverted1-3` or `*Constant*`.
 *
 * Paper stays quiet. Posting a claim is an ordinary outcome, not a warning, so colour is spent
 * only on the two states an operator acts on: electronic because it is the good one, and
 * "Not routed" because nothing can be sent at all.
 */
const TONE = {
    electronic: {
        fg: Variables.ThemeLight.PrimaryConstant6,
        bg: Variables.ThemeLight.PrimaryMild2,
        border: 'transparent',
    },
    paper: {
        fg: Variables.ThemeLight.TextMedium,
        bg: Variables.ThemeLight.Background1,
        border: Variables.ThemeLight.Border1,
    },
    blocked: {
        fg: Variables.ThemeLight.WarningInverted4,
        bg: Variables.ThemeLight.WarningMild3,
        border: 'transparent',
    },
};

export const Chip = styled.span<{ tone: Tone }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border-radius: 5px;
    font: 600 11px/1.4 var(--font-sans, inherit);
    white-space: nowrap;
    color: ${({ tone }) => TONE[tone].fg};
    background: ${({ tone }) => TONE[tone].bg};
    border: 1px solid ${({ tone }) => TONE[tone].border};
`;

export const Cell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`;

export const Destination = styled.span`
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMedium};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Detail = styled.span`
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMild};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/**
 * Zero is tinted rather than neutral: a payer with no EMR payer mapped receives nothing from
 * the client's system, which is the state worth noticing in a list.
 */
export const Count = styled.span<{ empty: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    padding: 3px 7px;
    border-radius: 5px;
    font: 600 12px/1.2 var(--font-sans, inherit);
    color: ${({ empty }) => (empty ? Variables.ThemeLight.WarningInverted4 : Variables.ThemeLight.TextStrong)};
    background: ${({ empty }) => (empty ? Variables.ThemeLight.WarningMild3 : Variables.ThemeLight.Background1)};
    border: 1px solid ${({ empty }) => (empty ? 'transparent' : Variables.ThemeLight.Border1)};
`;
